import { z } from 'zod'
import {
  criticidadeApiaSchema,
  fotoSchema,
  respostaSchema,
  statusInspecaoSchema,
} from './tipos.js'

/// Contrato entre o dispositivo e o servidor. É a fronteira mais delicada do sistema:
/// o cliente pode estar enviando algo que gravou há três dias, de uma versão anterior
/// do app, e reenviando pela quarta vez.

/// Uma inspeção saindo do dispositivo para o servidor.
export const envioInspecaoSchema = z.object({
  /// UUID v7 gerado no dispositivo. É a identidade definitiva — o servidor não
  /// inventa outra.
  id: z.string().uuid(),
  /// Gerado no dispositivo a cada tentativa de envio de um estado. Garante que
  /// reenviar após timeout não crie duplicata.
  clientMutationId: z.string().uuid(),
  codigo: z.string().min(1).max(64),
  checklistId: z.string().uuid(),
  obraId: z.string().uuid(),
  status: statusInspecaoSchema,
  titulo: z.string().max(400).nullable().optional(),
  dataCriacao: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  assinaturaUsuario: z.string().max(100).nullable().optional(),
  /// Quando o dispositivo gravou. Pode ser bem anterior ao momento do envio.
  registradoEm: z.string().datetime(),
  /// Versão que o dispositivo leu. `null` significa "estou criando".
  versaoBase: z.number().int().nullable().optional(),
  respostas: z.array(respostaSchema).default([]),
  fotos: z.array(fotoSchema).default([]),
})
export type EnvioInspecao = z.infer<typeof envioInspecaoSchema>

export const loteEnvioSchema = z.object({
  inspecoes: z.array(envioInspecaoSchema).max(50),
})
export type LoteEnvio = z.infer<typeof loteEnvioSchema>

export const resultadoEnvioSchema = z.discriminatedUnion('resultado', [
  z.object({
    resultado: z.literal('ok'),
    id: z.string().uuid(),
    versao: z.number().int(),
    /// Arquivos que o servidor ainda não tem e o dispositivo precisa enviar.
    arquivosPendentes: z.array(z.string().uuid()).default([]),
  }),
  z.object({
    resultado: z.literal('conflito'),
    id: z.string().uuid(),
    versaoServidor: z.number().int(),
    /// Estado atual no servidor, para o app mostrar as duas versões ao usuário.
    atual: z.unknown(),
  }),
  z.object({
    resultado: z.literal('rejeitado'),
    id: z.string().uuid(),
    /// Por que não entrou. O app mostra ao usuário em vez de tentar de novo para
    /// sempre — retentar não conserta um campo obrigatório em branco.
    problemas: z.array(z.object({ campoNome: z.string(), mensagem: z.string() })),
  }),
])
export type ResultadoEnvio = z.infer<typeof resultadoEnvioSchema>

export const respostaLoteSchema = z.object({
  resultados: z.array(resultadoEnvioSchema),
})
export type RespostaLote = z.infer<typeof respostaLoteSchema>

/// Descida de cadastros. `desde` é o `atualizadoEm` do último pull bem-sucedido;
/// o servidor devolve só o que mudou depois disso.
export const consultaCadastrosSchema = z.object({
  desde: z.string().datetime().optional(),
})
export type ConsultaCadastros = z.infer<typeof consultaCadastrosSchema>

export const pacoteCadastrosSchema = z.object({
  /// Carimbo do servidor. O cliente guarda e manda de volta no próximo `desde`.
  ate: z.string().datetime(),
  /// `true` quando o servidor mandou tudo em vez de um delta (primeiro pull, ou
  /// `desde` velho demais). O cliente apaga o cache local antes de aplicar.
  completo: z.boolean(),
  obras: z.array(z.unknown()).default([]),
  checklists: z.array(z.unknown()).default([]),
  pessoas: z.array(z.object({ email: z.string(), nome: z.string() })).default([]),
  /// Ids removidos desde `desde`, para o cliente limpar o cache.
  removidos: z.object({
    obras: z.array(z.string().uuid()).default([]),
    checklists: z.array(z.string().uuid()).default([]),
  }).default({ obras: [], checklists: [] }),
})
export type PacoteCadastros = z.infer<typeof pacoteCadastrosSchema>

export const revisaoCriticidadeSchema = z.object({
  criticidadeApia: criticidadeApiaSchema,
  versaoBase: z.number().int(),
})
export type RevisaoCriticidade = z.infer<typeof revisaoCriticidadeSchema>

/// Estados possíveis de um item na fila de saída do dispositivo.
export const EstadoFila = {
  PENDENTE: 'PENDENTE',
  ENVIANDO: 'ENVIANDO',
  ENVIADO: 'ENVIADO',
  /// Precisa de ação do usuário (conflito ou rejeição). Não retenta sozinho.
  BLOQUEADO: 'BLOQUEADO',
} as const
export type EstadoFila = (typeof EstadoFila)[keyof typeof EstadoFila]

/// Espera antes da próxima tentativa, em milissegundos. Cresce até 5 minutos e para
/// de crescer — numa obra sem sinal, tentar de hora em hora não ajuda ninguém.
export function esperaRetentativa(tentativa: number): number {
  const base = Math.min(2 ** tentativa * 1000, 5 * 60 * 1000)
  return base + Math.floor(Math.random() * 1000) // jitter, para 40 celulares não baterem juntos
}
